import { motion } from 'framer-motion';
import { Palette, Wand2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QRDesignSettings } from '@/stores/qrStore';
import { designPresets } from '@/lib/design-constants';

interface BatchDesignStepProps {
  designSettings: Partial<QRDesignSettings>;
  designMode: 'single' | 'preset' | 'per-row';
  selectedPreset: string;
  onDesignChange: (settings: Partial<QRDesignSettings>) => void;
  onModeChange: (mode: 'single' | 'preset' | 'per-row') => void;
  onPresetChange: (preset: string) => void;
  previewCount: number;
}

export function BatchDesignStep({
  designSettings,
  designMode,
  selectedPreset,
  onDesignChange,
  onModeChange,
  onPresetChange,
  previewCount,
}: BatchDesignStepProps) {
  const handleColorChange = (key: keyof QRDesignSettings, value: string) => {
    onDesignChange({ ...designSettings, [key]: value });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Design Mode Selection */}
      <div className="surface-elevated rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Design Application</h3>
        </div>
        
        <RadioGroup
          value={designMode}
          onValueChange={(value) => onModeChange(value as 'single' | 'preset' | 'per-row')}
          className="grid gap-4"
        >
          <div className="flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer">
            <RadioGroupItem value="preset" id="preset" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="preset" className="font-medium cursor-pointer">
                Use Design Preset
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Apply a pre-made design template to all QR codes
              </p>
              
              {designMode === 'preset' && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {designPresets.map((preset) => (
                    <div
                      key={preset.id}
                      onClick={() => onPresetChange(preset.id)}
                      className={`
                        p-3 rounded-lg border-2 cursor-pointer transition-all
                        ${selectedPreset === preset.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/30'}
                      `}
                    >
                      <div
                        className="w-full h-8 rounded mb-2"
                        style={{ backgroundColor: preset.dotsColor }}
                      />
                      <div className="text-sm font-medium">{preset.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer">
            <RadioGroupItem value="single" id="single" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="single" className="font-medium cursor-pointer">
                Custom Design for All
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Apply the same custom colors to all QR codes
              </p>
              
              {designMode === 'single' && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dots-color">QR Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        id="dots-color"
                        value={designSettings.dotsColor || '#1a1a2e'}
                        onChange={(e) => handleColorChange('dotsColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={designSettings.dotsColor || '#1a1a2e'}
                        onChange={(e) => handleColorChange('dotsColor', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bg-color">Background</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        id="bg-color"
                        value={designSettings.backgroundColor || '#ffffff'}
                        onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={designSettings.backgroundColor || '#ffffff'}
                        onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer">
            <RadioGroupItem value="per-row" id="per-row" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="per-row" className="font-medium cursor-pointer">
                Design Per Row (CSV Column)
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Use 'design_preset' column in your CSV to specify preset per QR
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Preview Info */}
      <div className="text-center text-sm text-muted-foreground">
        <Wand2 className="h-4 w-4 inline-block mr-2" />
        Design will be applied to {previewCount} QR codes
      </div>
    </motion.div>
  );
}
